import { useState, useEffect } from "react";
import { useRefundApplication } from "@/hooks/useRefundApplication";
import type { RefundApplication as RefundApplicationType } from "@/http/refundApplicationsApi";
import { formatDateShort } from "@/utils/dateFormatters";
import BreadcrumbComponent from "@/components/BreadcrumbComponent";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import type { RefundReason } from "@/@types/refund.types";
import { formatCurrency } from "@/utils/formatters";
import { REFUND_APPLICATION_STATE_CONFIG } from "@/utils/applicationStatusConfig";
import { Clock, CheckCircle2, Plus, Loader2 } from "lucide-react";

const RefundApplication = () => {
  const [showForm, setShowForm] = useState(false);
  const [selectedApp, setSelectedApp] = useState<RefundApplicationType | null>(null);

  const {
    formData,
    showOtherInput,
    isSubmitted,
    isSubmitting,
    successDialogOpen,
    setSuccessDialogOpen,
    activeTickets,
    ticketsLoading,
    handleTicketChange,
    handleReasonChange,
    handleOtherReasonChange,
    handleSubmit,
    handleClearApplication,
    refundReasons,
    refundCalculation,
    isCalculationLoading,
    existingApplications,
    applicationsLoading,
  } = useRefundApplication();

  // Scroll to top when view changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [showForm, selectedApp]);

  const handleSuccessClose = () => {
    setSuccessDialogOpen(false);
    handleClearApplication();
    setShowForm(false);
  };

  const handleSuccessDialogChange = (open: boolean) => {
    if (!open) {
      handleSuccessClose();
      return;
    }

    setSuccessDialogOpen(true);
  };

  const handleBack = () => {
    setShowForm(false);
    handleClearApplication();
  };

  // Detail view for selected application
  if (selectedApp) {
    const config =
      REFUND_APPLICATION_STATE_CONFIG[selectedApp.state] || REFUND_APPLICATION_STATE_CONFIG.new;

    return (
      <div className="flex flex-col p-3">
        <BreadcrumbComponent
          link="Заявление на возврат"
          onLinkClick={() => setSelectedApp(null)}
          secondLink="Детали заявления"
        />

        <div className="mx-0 my-4 sm:m-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <Badge variant={config.variant}>{config.label}</Badge>
                <span className="text-sm text-muted-foreground">
                  {formatDateShort(selectedApp.createdAt)}
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedApp.ticket_title && (
                <div>
                  <Label className="text-muted-foreground">Абонемент</Label>
                  <p className="font-medium">{selectedApp.ticket_title}</p>
                </div>
              )}

              <div>
                <Label className="text-muted-foreground">Причина возврата</Label>
                <p>{selectedApp.reason}</p>
              </div>

              {selectedApp.other_reason && (
                <div>
                  <Label className="text-muted-foreground">Дополнительная информация</Label>
                  <p>{selectedApp.other_reason}</p>
                </div>
              )}

              {(selectedApp.purchase_price || selectedApp.refund_amount) && (
                <div className="space-y-2 rounded-lg border bg-muted/50 p-4">
                  <Label className="text-muted-foreground">Расчёт суммы возврата</Label>
                  {selectedApp.purchase_price && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Стоимость абонемента:</span>
                      <span>{formatCurrency(selectedApp.purchase_price)}</span>
                    </div>
                  )}
                  {selectedApp.initial_sessions && selectedApp.used_sessions !== undefined && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Использовано занятий:</span>
                      <span>
                        {selectedApp.used_sessions} из {selectedApp.initial_sessions}
                      </span>
                    </div>
                  )}
                  {selectedApp.refund_amount !== undefined &&
                    selectedApp.refund_amount !== null && (
                      <div className="mt-2 flex justify-between border-t pt-2 text-base font-semibold">
                        <span>Сумма к возврату:</span>
                        <span className="text-primary">
                          {formatCurrency(selectedApp.refund_amount)}
                        </span>
                      </div>
                    )}
                </div>
              )}

              {selectedApp.manager_comment && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <Label className="text-blue-900">Комментарий менеджера</Label>
                  <p className="mt-1 text-sm text-blue-800">{selectedApp.manager_comment}</p>
                </div>
              )}
            </CardContent>
            <CardFooter>
              <Button variant="outline" onClick={() => setSelectedApp(null)} className="w-full">
                Назад к списку
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  // Form view
  if (showForm) {
    return (
      <div className="flex flex-col p-3">
        <BreadcrumbComponent
          link="Заявление на возврат"
          onLinkClick={handleBack}
          secondLink="Новое заявление"
        />
        <Card className="m-6 p-6">
          <form onSubmit={handleSubmit}>
            <CardHeader>
              <CardTitle>Новое заявление на возврат</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label className="text-base">
                  Выберите абонемент для возврата
                  <span className="ml-1 text-red-500">*</span>
                </Label>

                <Select
                  value={formData.ticketId}
                  onValueChange={handleTicketChange}
                  disabled={ticketsLoading}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue
                      placeholder={ticketsLoading ? "Загрузка..." : "Выберите абонемент"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {activeTickets.map((ticket) => (
                      <SelectItem key={ticket.ticket_id} value={ticket.ticket_id}>
                        {ticket.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {activeTickets.length === 0 && !ticketsLoading && (
                  <p className="text-sm text-muted-foreground">
                    У вас нет активных абонементов для возврата
                  </p>
                )}
              </div>

              {/* Refund calculation block */}
              {formData.ticketId && (
                <div className="space-y-3">
                  <Label className="text-base">Расчёт суммы возврата</Label>

                  {/* Refund rules */}
                  <div className="space-y-2 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm">
                    <p className="font-medium text-blue-900">Правила расчёта возврата:</p>
                    <ul className="list-inside list-disc space-y-1 text-blue-800">
                      <li>
                        Стоимость разового занятия —{" "}
                        {refundCalculation
                          ? formatCurrency(refundCalculation.singleSessionPrice)
                          : "..."}
                      </li>
                      <li>
                        Сумма возврата = стоимость абонемента − (использовано занятий × стоимость
                        разового занятия)
                      </li>
                      <li>Возврат производится за неиспользованные занятия</li>
                    </ul>
                  </div>

                  {isCalculationLoading ? (
                    <div className="rounded-lg border bg-muted/50 p-4">
                      <p className="text-sm text-muted-foreground">Загрузка данных...</p>
                    </div>
                  ) : refundCalculation ? (
                    <div className="space-y-2 rounded-lg border bg-muted/50 p-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Стоимость абонемента:</span>
                        <span className="font-medium">
                          {formatCurrency(refundCalculation.purchasePrice)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Начальное кол-во занятий:</span>
                        <span className="font-medium">{refundCalculation.initialSessions}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Осталось занятий:</span>
                        <span className="font-medium">{refundCalculation.remainingSessions}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Использовано занятий:</span>
                        <span className="font-medium">{refundCalculation.usedSessions}</span>
                      </div>
                      <div className="mt-2 flex justify-between border-t pt-2 text-sm">
                        <span className="text-muted-foreground">
                          Стоимость использованных занятий ({refundCalculation.usedSessions} ×{" "}
                          {formatCurrency(refundCalculation.singleSessionPrice)}):
                        </span>
                        <span className="font-medium text-destructive">
                          −{formatCurrency(refundCalculation.usedAmount)}
                        </span>
                      </div>
                      <div className="mt-2 flex justify-between border-t pt-2 text-base font-semibold">
                        <span>Сумма к возврату:</span>
                        <span className="text-primary">
                          {formatCurrency(refundCalculation.refundAmount)}
                        </span>
                      </div>
                      <p className="mt-2 border-t pt-2 text-xs text-amber-600">
                        Калькулятор работает в тестовом режиме. Подробности уточняйте у
                        администратора.
                      </p>
                    </div>
                  ) : (
                    <Alert variant="info">
                      <AlertTitle>Расчёт недоступен</AlertTitle>
                      <AlertDescription>
                        Мы не смогли рассчитать сумму возврата. Менеджер свяжется с вами для
                        уточнения.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}

              <div className="space-y-3">
                <Label className="text-base">
                  Что стало основной причиной для возврата?
                  <span className="ml-1 text-red-500">*</span>
                </Label>

                <RadioGroup
                  value={formData.reason}
                  onValueChange={(value) => handleReasonChange(value as RefundReason)}
                >
                  {refundReasons.map((reason) => (
                    <div key={reason} className="flex items-center space-x-3">
                      <RadioGroupItem value={reason} id={reason} />
                      <Label htmlFor={reason} className="cursor-pointer font-normal">
                        {reason}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>

                {showOtherInput && (
                  <div className="ml-7 mt-4">
                    <Input
                      type="text"
                      placeholder="Пожалуйста, укажите причину"
                      value={formData.otherReason}
                      onChange={(e) => handleOtherReasonChange(e.target.value)}
                      className="w-full"
                      required
                    />
                  </div>
                )}
              </div>

              {formData.ticketId && formData.reason && (
                <Alert variant="muted">
                  <AlertDescription className="text-xs">
                    После отправки заявления с вами свяжется менеджер для уточнения деталей
                    возврата.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
            <CardFooter className="flex gap-3">
              <Button
                type="submit"
                className="flex-1"
                disabled={
                  !formData.ticketId ||
                  !formData.reason ||
                  (showOtherInput && !formData.otherReason.trim()) ||
                  isSubmitting ||
                  isSubmitted
                }
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Отправка...
                  </>
                ) : (
                  "Отправить заявление"
                )}
              </Button>
              <Button type="button" variant="outline" onClick={handleBack}>
                Отмена
              </Button>
            </CardFooter>
          </form>
        </Card>

        <Dialog open={successDialogOpen} onOpenChange={handleSuccessDialogChange}>
          <DialogContent>
            <DialogHeader>
              <div className="mx-auto mb-4 rounded-full bg-green-100 p-3">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <DialogTitle className="text-center">Заявление отправлено</DialogTitle>
              <DialogDescription className="text-center">
                Ваше заявление на возврат успешно отправлено. Мы свяжемся с вами в ближайшее время
                для уточнения деталей.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={handleSuccessClose} className="w-full">
                Понятно
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Main view with history
  return (
    <div className="flex flex-col p-3">
      <BreadcrumbComponent link="Заявление на возврат" />

      <div className="mx-0 my-4 space-y-6 sm:m-6">
        {/* Header with action button */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Заявление на возврат</h1>
            <p className="text-muted-foreground">
              Подайте заявление на возврат средств за абонемент
            </p>
          </div>
          <Button onClick={() => setShowForm(true)} className="w-full gap-2 sm:w-auto">
            <Plus className="h-4 w-4" />
            Новое заявление
          </Button>
        </div>

        {/* Applications list */}
        {applicationsLoading ? (
          <Card>
            <CardContent className="py-12">
              <div className="flex flex-col items-center justify-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <p className="text-muted-foreground">Загрузка заявлений...</p>
              </div>
            </CardContent>
          </Card>
        ) : existingApplications && existingApplications.length > 0 ? (
          <div className="space-y-4">
            <h2 className="text-lg font-medium">Мои заявления ({existingApplications.length})</h2>
            {existingApplications.map((app) => {
              const config =
                REFUND_APPLICATION_STATE_CONFIG[app.state] || REFUND_APPLICATION_STATE_CONFIG.new;

              return (
                <Card key={app.id} className="cursor-pointer" onClick={() => setSelectedApp(app)}>
                  <CardContent className="space-y-2 p-3">
                    <Badge variant={config.variant} className="text-xs">
                      {config.label}
                    </Badge>

                    {app.ticket_title && (
                      <h3 className="text-sm font-medium leading-snug">{app.ticket_title}</h3>
                    )}

                    <p className="text-sm">
                      <span className="text-muted-foreground">Причина: </span>
                      {app.reason}
                    </p>

                    {app.refund_amount !== undefined && app.refund_amount !== null && (
                      <p className="text-sm font-medium text-primary">
                        Сумма к возврату: {formatCurrency(app.refund_amount)}
                      </p>
                    )}

                    <div className="pt-1 text-xs text-muted-foreground">
                      {formatDateShort(app.createdAt)}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12">
              <div className="flex flex-col items-center justify-center gap-3 text-center">
                <div className="rounded-full bg-muted p-3">
                  <Clock className="h-8 w-8 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium">Заявлений пока нет</p>
                  <p className="text-sm text-muted-foreground">
                    Нажмите кнопку выше, чтобы подать заявление на возврат
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default RefundApplication;
